import { rentalsExposer } from '@pirates/not-morty-biz';
import { startOfMonth, endOfMonth, startOfDay } from 'date-fns';
import { getGMTDate } from '@/utils/date';
import { ActionContext, ActionTree, Module, MutationTree } from 'vuex';
import { RentalState, InvoiceDownloadParams, LoadRentalActionParams } from './rentals.model';
import { rentalsService } from './rentals.service';
import { GETTER_CURRENT_COMPANY_ID, GETTER_IS_COMPANY_CENTRAL_PAYMENT } from '../company/company.store';

enum DEFAULT_GET_COMPANY_INVOICES_PARAMS {
  page = 0,
  size = 999,
}

class Rentals implements Module<RentalState, any> {
  public state: RentalState = {
    companyPaymentProfiles: [],
    drivers: [],
    monthlyInvoiceUrls: [],
  };
  public namespaced: boolean = true;

  public actions = {
    async loadDrivers(store: ActionContext<RentalState, any>) {
      const drivers = await rentalsExposer.drivers;
      store.commit('SET_DRIVERS', drivers);
    },
    async loadCompanyDrivers(store: ActionContext<RentalState, any>, keyword: string = '') {
      const drivers = await rentalsExposer.companyDrivers;
      const filteredDrivers = drivers.filter((driver) => driver.displayName.toLowerCase().includes(keyword.toLowerCase()));

      store.commit('SET_DRIVERS', filteredDrivers);
    },
    async loadCompanyPaymentProfiles(store: ActionContext<RentalState, any>) {
      if (store.state.companyPaymentProfiles.length) {
        return;
      }

      try {
        const paymentProfiles = await rentalsService.getPaymentProfiles(store.rootGetters[GETTER_CURRENT_COMPANY_ID]);
        store.commit('SET_COMPANY_PAYMENT_PROFILES', paymentProfiles);
      } catch (error) {
        store.dispatch('app/showServerError', error, { root: true });
      }
    },

    async loadCompanyPerformingState(store: ActionContext<RentalState, any>) {
      if (store.state.performingState) {
        return;
      }

      try {
        const refCompanyId = store.rootGetters[GETTER_CURRENT_COMPANY_ID];
        const { performingState } = await rentalsService.getPaymentProfileOwner(refCompanyId);
        store.commit('SET_COMPANY_PERFORMING_STATE', performingState);
      } catch (error) {
        store.dispatch('app/showServerError', error, { root: true });
      }
    },

    async loadMonthlyInvoiceUrl(store: ActionContext<RentalState, any>, { month, paymentProfileIds }: LoadRentalActionParams) {
      const isCentral = store.rootGetters[GETTER_IS_COMPANY_CENTRAL_PAYMENT];
      if (!isCentral) {
        store.commit('SET_MONTHLY_INVOICE_URLS', []);
        return [];
      }

      const refCompanyId = store.rootGetters[GETTER_CURRENT_COMPANY_ID];
      const page = DEFAULT_GET_COMPANY_INVOICES_PARAMS.page;
      const size = DEFAULT_GET_COMPANY_INVOICES_PARAMS.size;

      // We need to pass start and end of the month in UTC
      const [start, end] = [startOfMonth(month), endOfMonth(month)];
      const dateFrom = new Date(Date.UTC(start.getFullYear(), start.getMonth(), 1, 0, 0, 0, 0)).toISOString();
      const dateTo = new Date(Date.UTC(end.getFullYear(), end.getMonth(), end.getDate(), 23, 59, 59, 999)).toISOString();

      const invoices = await rentalsService.getCompanyInvoices({ page, size, dateFrom, dateTo, refCompanyId, paymentProfileIds });
      store.commit('SET_MONTHLY_INVOICE_URLS', invoices);
      return invoices;
    },

    /* tslint:disable:no-unused */
    loadOwnRentalsByMonth(_: ActionContext<RentalState, any>, month: Date) {
      return rentalsExposer.ownRentalsByMonth(month);
    },
    loadAllRentalsByParams(_: ActionContext<RentalState, any>, params) {
      return rentalsExposer.allRentalsByParams(params);
    },
    loadCompanyRentalsByParams(store: ActionContext<RentalState, any>, params) {
      params.id = store.rootGetters[GETTER_CURRENT_COMPANY_ID];
      const isCentral: boolean = store.rootGetters[GETTER_IS_COMPANY_CENTRAL_PAYMENT];

      const month = startOfDay(new Date(params.to));
      params.from = new Date(params.from);
      params.to = new Date(params.to);
      if (isCentral) {
        return rentalsService.getRentals(params);
      }

      params.from = getGMTDate(startOfMonth(month)).toISOString();
      params.to = getGMTDate(endOfMonth(month)).toISOString();
      return rentalsService.getAllRentals(params);
    },
    getRentalsByParams(store: ActionContext<RentalState, any>, params) {
      params.id = store.rootGetters[GETTER_CURRENT_COMPANY_ID];
      const isCentral: boolean = store.rootGetters[GETTER_IS_COMPANY_CENTRAL_PAYMENT];

      params.to = getGMTDate(startOfDay(endOfMonth(new Date(params.to))));
      params.from = getGMTDate(startOfDay(startOfMonth(new Date(params.from))));

      if (isCentral) {
        return rentalsService.getRentals(params);
      }

      params.to = params.to.toISOString();
      params.from = params.from.toISOString();

      return rentalsService.getAllRentals(params);
    },
    loadRentalById(_: ActionContext<RentalState, any>, uuid) {
      return rentalsExposer.rentalById(uuid);
    },
    downloadRentalInvoice(_: ActionContext<RentalState, any>, params: InvoiceDownloadParams) {
      return rentalsService.downloadInvoice(params);
    },
    downloadMonthlyRentalInvoice(_: ActionContext<RentalState, any>) {
      return;
    },
    loadCompanySpecialPaymentsByFilters(store: ActionContext<RentalState, any>, { from, to, customerId }: { from: Date; to: Date; customerId?: number }) {
      const isCentral: boolean = store.rootGetters[GETTER_IS_COMPANY_CENTRAL_PAYMENT];
      if (isCentral) {
        return rentalsService.getSpecialPayments(store.rootGetters[GETTER_CURRENT_COMPANY_ID], from, to, customerId);
      }

      return [];
    },
  } as ActionTree<RentalState, any>;

  public mutations = {
    SET_DRIVERS(state: RentalState, payload) {
      state.drivers = payload;
    },
    SET_COMPANY_PAYMENT_PROFILES(state: RentalState, paymentProfiles) {
      state.companyPaymentProfiles = paymentProfiles;
    },
    SET_COMPANY_PERFORMING_STATE(state: RentalState, performingState) {
      state.performingState = performingState;
    },
    SET_MONTHLY_INVOICE_URLS(state: RentalState, payload) {
      state.monthlyInvoiceUrls = payload;
    },
  } as MutationTree<RentalState>;
}

export default new Rentals();
